<template>
  <div class="p-4 md:p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-5">
      <div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Carte condivise</h2>
        <p class="text-sm text-gray-500 mt-0.5">
          Carte fisiche usate da più account · ordinate per importo non pagato
        </p>
      </div>
      <button
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-white text-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 text-sm transition"
        :disabled="loading"
        @click="refresh"
      >
        <span class="material-icons text-sm" :class="{ 'animate-spin': loading }">refresh</span>
        Aggiorna
      </button>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="space-y-3">
      <div
        v-for="i in 5"
        :key="i"
        class="h-24 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"
      ></div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!clusters.length"
      class="bg-white dark:bg-gray-900 rounded-xl shadow p-12 text-center text-gray-400"
    >
      <span class="material-icons text-5xl mb-3 block">credit_score</span>
      <p class="text-lg font-medium">Nessun cluster sospetto trovato.</p>
      <p class="text-sm mt-1">Non ci sono carte condivise tra più account.</p>
    </div>

    <!-- Lista cluster -->
    <div v-else class="space-y-3">
      <div
        v-for="cluster in clusters"
        :key="cluster.card_fingerprint"
        class="bg-white dark:bg-gray-900 rounded-xl shadow overflow-hidden"
      >
        <!-- Header cluster -->
        <div class="p-4 flex items-center gap-4 flex-wrap">
          <!-- Icona carta + info -->
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <span class="material-icons text-3xl text-gray-400">credit_card</span>
            <div>
              <p class="font-semibold text-gray-900 dark:text-white">
                {{ cardLabel(cluster) }}
                <span
                  v-if="cluster.fraud_whitelist"
                  class="ml-2 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded-full"
                >
                  ✓ Legittima
                </span>
              </p>
              <p class="text-sm text-gray-500">
                Usata da
                <span class="font-medium text-gray-800 dark:text-gray-200">{{ cluster.account_count }} account</span>
                ·
                <span
                  :class="cluster.total_unpaid_eur > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-400'"
                >
                  {{ cluster.total_unpaid_eur > 0 ? formatEur(cluster.total_unpaid_eur) + ' non pagati' : '0 non pagati' }}
                </span>
                ·
                <span :class="cluster.total_success_tx > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'">
                  {{ cluster.total_success_tx }} pagamenti OK
                </span>
              </p>
            </div>
          </div>

          <!-- Azioni -->
          <div class="flex items-center gap-2 flex-shrink-0">
            <!-- Toggle lista account -->
            <button
              class="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition text-gray-700 dark:text-gray-300"
              @click="toggleExpand(cluster.card_fingerprint)"
            >
              <span class="material-icons text-sm">
                {{ expanded.has(cluster.card_fingerprint) ? 'expand_less' : 'expand_more' }}
              </span>
              Vedi account
            </button>

            <!-- Whitelist toggle -->
            <button
              v-if="!cluster.fraud_whitelist"
              class="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white transition"
              @click="openWhitelistModal(cluster, true)"
            >
              <span class="material-icons text-sm">verified</span>
              Marca legittima
            </button>
            <button
              v-else
              class="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20 transition"
              @click="openWhitelistModal(cluster, false)"
            >
              <span class="material-icons text-sm">remove_circle_outline</span>
              Rimuovi legittimità
            </button>
          </div>
        </div>

        <!-- Account espansi -->
        <div
          v-if="expanded.has(cluster.card_fingerprint)"
          class="border-t border-gray-100 dark:border-gray-800"
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-500 uppercase">
                <th class="px-4 py-2 text-left">Account</th>
                <th class="px-4 py-2 text-left hidden md:table-cell">Email</th>
                <th class="px-4 py-2 text-left hidden lg:table-cell">Iscritto</th>
                <th class="px-4 py-2 text-right">Non pagato</th>
                <th class="px-4 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
              <tr
                v-for="acc in cluster.accounts"
                :key="acc.uid"
                class="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition"
              >
                <td class="px-4 py-2.5 font-medium text-gray-900 dark:text-white">
                  {{ acc.name }} {{ acc.surname }}
                  <span class="text-xs text-gray-400 ml-1">#{{ acc.uid }}</span>
                </td>
                <td class="px-4 py-2.5 text-gray-500 hidden md:table-cell">{{ acc.email }}</td>
                <td class="px-4 py-2.5 text-gray-400 text-xs hidden lg:table-cell">
                  {{ relativeDate(acc.created_at) }}
                </td>
                <td class="px-4 py-2.5 text-right">
                  <span
                    v-if="acc.unpaid_eur > 0"
                    class="text-red-600 dark:text-red-400 font-medium"
                  >
                    {{ formatEur(acc.unpaid_eur) }}
                  </span>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="px-4 py-2.5 text-right">
                  <router-link
                    :to="{ name: 'menu.antifrode.utenti.detail', params: { uid: acc.uid } }"
                    class="text-blue-600 hover:underline text-xs"
                  >
                    Dettaglio →
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal whitelist conferma -->
    <flow-modal v-if="wlModal.open" @backdrop="closeWlModal">
      <template #header>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ wlModal.addWhitelist ? 'Marca carta come legittima' : 'Rimuovi flag legittimità' }}
        </h3>
      </template>
      <template #body>
        <!-- Avviso impatto cluster -->
        <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mb-4">
          <p class="text-sm text-amber-800 dark:text-amber-300 font-medium">
            <span class="material-icons text-sm align-middle mr-1">info</span>
            Questa azione riguarda
            <strong>{{ wlModal.cluster?.account_count }} account</strong>:
          </p>
          <ul class="mt-2 text-sm text-amber-700 dark:text-amber-400 space-y-0.5 ml-4">
            <li
              v-for="acc in (wlModal.cluster?.accounts || []).slice(0, 10)"
              :key="acc.uid"
            >
              • {{ acc.name }} {{ acc.surname }} ({{ acc.email }})
            </li>
            <li
              v-if="(wlModal.cluster?.accounts || []).length > 10"
              class="italic"
            >
              ... e altri {{ wlModal.cluster.accounts.length - 10 }} account
            </li>
          </ul>
        </div>

        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Motivazione <span class="text-red-500">*</span>
        </label>
        <textarea
          v-model="wlModal.reason"
          class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-green-500 focus:outline-none"
          rows="3"
          placeholder="Es: Carta aziendale FlottaPro – confermato da Mario il 29/05"
        ></textarea>
        <p v-if="wlModal.reason.length > 0 && wlModal.reason.length < 5" class="text-xs text-red-500 mt-1">
          Minimo 5 caratteri.
        </p>
      </template>
      <template #footer>
        <button
          class="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800 transition"
          @click="closeWlModal"
        >
          Annulla
        </button>
        <button
          class="px-4 py-2 text-sm rounded-lg text-white transition disabled:opacity-50"
          :class="wlModal.addWhitelist ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'"
          :disabled="wlModal.reason.length < 5 || wlModal.loading"
          @click="confirmWhitelist"
        >
          {{ wlModal.loading ? 'In corso...' : (wlModal.addWhitelist ? 'Conferma – Marca legittima' : 'Conferma – Rimuovi') }}
        </button>
      </template>
    </flow-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      expanded: new Set(),
      wlModal: {
        open: false,
        cluster: null,
        addWhitelist: true,
        reason: '',
        loading: false,
      },
    };
  },
  computed: {
    ...mapGetters(['antifrode_cardClusters', 'antifrode_loading']),
    clusters() {
      return this.antifrode_cardClusters || [];
    },
    loading() {
      return this.antifrode_loading;
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.$store.dispatch('fetchAntifrodeCardClusters');
    },
    toggleExpand(fingerprint) {
      if (this.expanded.has(fingerprint)) {
        this.expanded.delete(fingerprint);
      } else {
        this.expanded.add(fingerprint);
      }
      // Forza reattività su Set
      this.expanded = new Set(this.expanded);
    },
    openWhitelistModal(cluster, addWhitelist) {
      this.wlModal = { open: true, cluster, addWhitelist, reason: '', loading: false };
    },
    closeWlModal() {
      this.wlModal = { open: false, cluster: null, addWhitelist: true, reason: '', loading: false };
    },
    async confirmWhitelist() {
      this.wlModal.loading = true;
      try {
        await this.$store.dispatch('antifrode_toggleWhitelistCard', {
          cardId: this.wlModal.cluster.representative_card_id,
          whitelist: this.wlModal.addWhitelist,
          reason: this.wlModal.reason,
        });
        this.closeWlModal();
        this.refresh();
      } catch (e) {
        console.error('Whitelist fallita', e);
        this.wlModal.loading = false;
      }
    },
    // ── helpers UI ────────────────────────────────────────────────
    cardLabel(cluster) {
      const brand = cluster.brand
        ? cluster.brand.charAt(0).toUpperCase() + cluster.brand.slice(1).toLowerCase()
        : 'Carta';
      const last4 = cluster.last4 ? `****${cluster.last4}` : '';
      const exp =
        cluster.exp_month && cluster.exp_year
          ? `scad. ${String(cluster.exp_month).padStart(2, '0')}/${cluster.exp_year}`
          : '';
      return [brand, last4, exp].filter(Boolean).join('  ');
    },
    formatEur(val) {
      if (!val) return '€0';
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }).format(val);
    },
    relativeDate(dateStr) {
      if (!dateStr) return '—';
      const diffMs = Date.now() - new Date(dateStr).getTime();
      const days = Math.floor(diffMs / 86400000);
      if (days === 0) return 'oggi';
      if (days === 1) return 'ieri';
      if (days < 30) return `${days} gg fa`;
      if (days < 365) return `${Math.floor(days / 30)} mesi fa`;
      return `${Math.floor(days / 365)} anni fa`;
    },
  },
};
</script>

<style></style>
